import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Truck,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Search,
  RefreshCw,
  Eye,
  FileText,
  ChevronLeft,
  ChevronRight,
  Trash2
} from 'lucide-react';
import { v1ApiClient } from '../services/api';
import OnboardingApplicationDetailModal from '../components/superadmin/OnboardingApplicationDetailModal';

export const SuperAdminApprovalsQueue = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState('ALL'); // ALL | HOSPITAL | FLEET
  const [statusFilter, setStatusFilter] = useState('SUBMITTED'); // SUBMITTED | DRAFT | APPROVED | REJECTED | ALL
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, limit: 20 });

  // Decision Action Box ('changes' | 'reject' | null)
  const [decisionAction, setDecisionAction] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [decisionError, setDecisionError] = useState('');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (typeFilter !== 'ALL') params.type = typeFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const response = await v1ApiClient.get('/admin/onboarding/applications', { params });
      const data = response.data?.data?.applications || response.data?.applications || [];
      const pag = response.data?.data?.pagination || response.data?.pagination || {
        total: data.length,
        totalPages: 1,
        page: 1,
        limit: 20
      };
      setApplications(data);
      setPagination(pag);
    } catch (err) {
      console.error('Failed to load onboarding applications:', err);
      showToast(err.response?.data?.message || 'Failed to load applications', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, searchQuery, page]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleOpenDetail = async (appId) => {
    setIsDetailLoading(true);
    setDecisionAction(null);
    setReviewNotes('');
    setDecisionError('');
    try {
      const response = await v1ApiClient.get(`/admin/onboarding/applications/${appId}`);
      setSelectedApp(response.data?.data || response.data);
    } catch (err) {
      console.error('Failed to fetch application detail:', err);
      showToast('Could not load application details', 'error');
    } finally {
      setIsDetailLoading(false);
    }
  };

  const handleApprove = async (approvalPayload = null) => {
    if (!selectedApp) return;
    if (!window.confirm(`Are you sure you want to approve "${selectedApp.name}"? This will provision operational records and activate live platform access.`)) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await v1ApiClient.post(
        `/admin/onboarding/applications/${selectedApp.application_id}/approve`,
        approvalPayload || {}
      );
      const loginEmail = response.data?.data?.loginEmail || response.data?.loginEmail || approvalPayload?.admin_email;
      showToast(
        loginEmail
          ? `Partner approved! Login Email: ${loginEmail}`
          : (response.data?.message || 'Application approved and operational records provisioned!'),
        'success'
      );
      setSelectedApp(null);
      fetchApplications();
    } catch (err) {
      console.error('Approval failed:', err);
      showToast(err.response?.data?.message || 'Approval transaction failed', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecisionSubmit = async () => {
    if (!reviewNotes.trim() || reviewNotes.trim().length < 5) {
      setDecisionError('Please enter at least 5 characters for review notes.');
      return;
    }

    setActionLoading(true);
    setDecisionError('');
    try {
      const endpoint = decisionAction === 'reject' ? 'reject' : 'request-changes';
      const response = await v1ApiClient.post(
        `/admin/onboarding/applications/${selectedApp.application_id}/${endpoint}`,
        { notes: reviewNotes.trim() }
      );
      showToast(
        response.data?.message || `Application updated to ${decisionAction === 'reject' ? 'REJECTED' : 'CHANGES_REQUESTED'}`,
        'success'
      );
      setDecisionAction(null);
      setReviewNotes('');
      setSelectedApp(null);
      fetchApplications();
    } catch (err) {
      console.error('Action failed:', err);
      setDecisionError(err.response?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteApp = async (appId, appName) => {
    if (!window.confirm(`Are you sure you want to delete "${appName || 'this application'}"? It will be permanently removed.`)) {
      return;
    }
    try {
      await v1ApiClient.delete(`/admin/onboarding/applications/${appId}`);
      showToast('Application deleted successfully.', 'success');
      fetchApplications();
    } catch (err) {
      console.error('Failed to delete application:', err);
      showToast(err.response?.data?.message || err.message || 'Failed to delete application', 'error');
    }
  };

  const changeRequestPresets = [
    'Please re-upload a clear copy of your registration certificate or trade license.',
    'Please verify bank account holder name and IFSC code details.',
    'Please update ambulance fleet capacities and driver roster details.',
    'Please specify your exact emergency coverage areas and operating pincodes.'
  ];

  const rejectionPresets = [
    'Incomplete documentation provided. Registration license cannot be verified.',
    'Service area outside current platform operational coverage.',
    'Failed background verification requirements.'
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 font-sans antialiased text-slate-700">
      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-[100] max-w-md animate-bounce-in">
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl text-white text-xs sm:text-sm font-semibold ${
              toast.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'
            }`}
          >
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 mb-2">
            <span>COMMAND DESK REVIEW</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight">
            Partner Onboarding Requests
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 font-normal">
            Review self-registered hospital and ambulance fleet partner applications before live network activation.
          </p>
        </div>
        <button
          onClick={fetchApplications}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-semibold text-xs shadow-xs transition-colors cursor-pointer"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Type Tabs */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl w-full md:w-auto">
          {['ALL', 'HOSPITAL', 'FLEET'].map((type) => (
            <button
              key={type}
              onClick={() => {
                setTypeFilter(type);
                setPage(1);
              }}
              className={`flex-1 md:flex-initial px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                typeFilter === type
                  ? 'bg-white text-blue-700 shadow-xs'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {type === 'ALL' ? 'All Partners' : type === 'HOSPITAL' ? 'Hospitals' : 'Fleets'}
            </button>
          ))}
        </div>

        {/* Status Dropdown & Search */}
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-auto px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-600"
          >
            <option value="SUBMITTED">Status: Pending Review (Submitted)</option>
            <option value="DRAFT">Status: In-Progress Drafts</option>
            <option value="CHANGES_REQUESTED">Status: Changes Requested</option>
            <option value="APPROVED">Status: Approved</option>
            <option value="REJECTED">Status: Rejected</option>
            <option value="ALL">Status: All Statuses</option>
          </select>

          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, city, code..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3.5 py-2 rounded-xl border border-slate-200 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600"
            />
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
            <span>Loading onboarding applications...</span>
          </div>
        ) : applications.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <FileText className="w-10 h-10 text-slate-300 mx-auto" />
            <h3 className="text-sm font-semibold text-slate-700">No applications found</h3>
            <p className="text-xs text-slate-400 max-w-sm mx-auto">
              There are currently no onboarding applications matching your selected status and filter criteria.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
                  <th className="py-3 px-4">Application Code</th>
                  <th className="py-3 px-4">Partner Entity</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Location</th>
                  <th className="py-3 px-4">Admin Contact</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {applications.map((app) => {
                  const isHosp = app.partner_type === 'HOSPITAL';
                  return (
                    <tr key={app.application_id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3.5 px-4 font-mono font-medium text-blue-700 select-all">
                        {String(app.application_code || '').replace(/\D/g, '') || app.application_code || 'DRAFT'}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-slate-800">{app.name}</div>
                        {/* Temporarily hidden: step completed progress label */}
                        {/* <div className="text-[11px] text-slate-400 font-normal">Step {app.current_step} completed</div> */}
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${
                            isHosp
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}
                        >
                          {isHosp ? <Building2 className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                          <span>{app.partner_type}</span>
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-slate-600">
                        {app.city ? `${app.city}, ${app.state || ''}` : '—'}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-medium text-slate-800">{app.admin_name}</div>
                        <div className="text-[11px] text-slate-400">{app.admin_email}</div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase ${
                            app.status === 'APPROVED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : app.status === 'SUBMITTED'
                              ? 'bg-amber-100 text-amber-800 animate-pulse'
                              : app.status === 'REJECTED'
                              ? 'bg-red-100 text-red-800'
                              : app.status === 'CHANGES_REQUESTED'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {app.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleOpenDetail(app.application_id)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs transition-colors cursor-pointer"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>Review</span>
                          </button>
                          <button
                            onClick={() => handleDeleteApp(app.application_id, app.name)}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-red-200 hover:border-red-300 bg-red-50 hover:bg-red-100 text-red-700 font-semibold text-xs transition-colors cursor-pointer"
                            title="Delete Application"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls Footer */}
            {pagination.total > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-3.5 border-t border-slate-200 bg-slate-50/70 gap-3">
                <span className="text-xs text-slate-500 font-medium">
                  Showing page <strong className="text-slate-800">{page}</strong> of <strong className="text-slate-800">{pagination.totalPages || 1}</strong> ({pagination.total} total applications)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <span>Previous</span>
                  </button>
                  <button
                    disabled={page >= (pagination.totalPages || 1) || loading}
                    onClick={() => setPage((p) => p + 1)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
                  >
                    <span>Next</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* STEP-WISE ONBOARDING APPLICATION DETAIL MODAL */}
      {/* ======================================================== */}
      {selectedApp && (
        <OnboardingApplicationDetailModal
          selectedApp={selectedApp}
          onClose={() => setSelectedApp(null)}
          onApprove={handleApprove}
          actionLoading={actionLoading}
          decisionAction={decisionAction}
          setDecisionAction={setDecisionAction}
          reviewNotes={reviewNotes}
          setReviewNotes={setReviewNotes}
          decisionError={decisionError}
          setDecisionError={setDecisionError}
          handleDecisionSubmit={handleDecisionSubmit}
          rejectionPresets={rejectionPresets}
          changeRequestPresets={changeRequestPresets}
        />
      )}
    </div>
  );
};

export default SuperAdminApprovalsQueue;
